import { DEFAULT_ENRICHMENT_ORDER, DEFAULT_ENRICHMENT_ORDER_BY } from '@/modules/enrichment/helpers';
import { toDiseaseClassIdParam } from '@/modules/enrichment/meshDiseaseClasses';

export const getEnrichmentPayload = (
  searchType: string,
  text: string,
  customParams?: any,
  geneFilterType: 'symbol' | 'ensemblId' | 'geneId' | 'uniprotId' | 'name' = 'symbol',
) => {
  const lines = String(text || '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  const defaultParams = {
    commonVariants: 2,
    commonGenes: 2,
    maxPvalue: 0.1,
    minScore: 0.1,
    pageNumber: 0,
    source: 'ALL',
    orderBy: DEFAULT_ENRICHMENT_ORDER_BY,
    order: DEFAULT_ENRICHMENT_ORDER,
  };

  const params = { ...defaultParams, ...(customParams || {}) };
  const diseaseClassID_MSH = toDiseaseClassIdParam(params.diseaseClassName_MSH);

  if (searchType === 'GENES') {
    const payload: Record<string, string | number> = {
      commonGenes: params.commonGenes ?? params.commonVariants ?? 2,
      geneENSEMBLList: '',
      geneNCBIList: '',
      geneHGNCList: '',
      geneUniProtList: '',
      maxPvalue: params.maxPvalue,
      minScore: params.minScore,
      pageNumber: params.pageNumber,
      source: params.source,
      orderBy: params.orderBy,
      order: params.order,
    };

    const joined = lines.join(',');

    if (geneFilterType === 'symbol' || geneFilterType === 'name') {
      payload.geneHGNCList = joined;
    } else if (geneFilterType === 'ensemblId') {
      payload.geneENSEMBLList = joined;
    } else if (geneFilterType === 'geneId') {
      payload.geneNCBIList = joined;
    } else if (geneFilterType === 'uniprotId') {
      payload.geneUniProtList = joined;
    }

    if (diseaseClassID_MSH) {
      payload.diseaseClassID_MSH = diseaseClassID_MSH;
    }

    return payload;
  }

  if (searchType === 'VARIANTS') {
    const payload: Record<string, string | number> = {
      commonVariants: params.commonVariants ?? 2,
      variantList: lines.join(','),
      maxPvalue: params.maxPvalue,
      minScore: params.minScore,
      pageNumber: params.pageNumber,
      source: params.source,
      orderBy: params.orderBy,
      order: params.order,
    };

    if (diseaseClassID_MSH) {
      payload.diseaseClassID_MSH = diseaseClassID_MSH;
    }

    return payload;
  }

  return params;
};

export const getGseaPayload = (
  text: string,
  customParams?: Record<string, unknown>,
  geneFilterType: 'symbol' | 'ensemblId' | 'geneId' | 'uniprotId' | 'name' = 'symbol',
) => {
  const lines = String(text || '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  const defaultParams = {
    source: 'ALL',
    minScore: 0,
    exponent: 1,
    minCommonGenes: 2,
    minGeneSetSize: 15,
    maxGeneSetSize: 5000,
    minHits: 1,
    nPermutations: 1,
  };

  const params = { ...defaultParams, ...(customParams || {}) };

  const payload: Record<string, string | number> = {
    geneENSEMBLList: '',
    geneNCBIList: '',
    geneHGNCList: '',
    geneUniProtList: '',
    source: String(params.source),
    minScore: Number(params.minScore),
    exponent: Number(params.exponent),
    minCommonGenes: Number(params.minCommonGenes),
    minGeneSetSize: Number(params.minGeneSetSize),
    maxGeneSetSize: Number(params.maxGeneSetSize),
    minHits: Number(params.minHits),
    nPermutations: Number(params.nPermutations),
  };

  const joined = lines.join(',');

  if (geneFilterType === 'symbol' || geneFilterType === 'name') {
    payload.geneHGNCList = joined;
  } else if (geneFilterType === 'ensemblId') {
    payload.geneENSEMBLList = joined;
  } else if (geneFilterType === 'geneId') {
    payload.geneNCBIList = joined;
  } else if (geneFilterType === 'uniprotId') {
    payload.geneUniProtList = joined;
  }

  return payload;
};

export function createGseaInfoContent({
  text,
  gseaParams,
  geneFilterType,
}: {
  text: string;
  gseaParams: Record<string, unknown>;
  geneFilterType: string;
}) {
  return `GSEA ANALYSIS INFORMATION
=========================

Download Date:   ${new Date().toLocaleString()}

QUERY
-----
Query Text:      ${text}

PARAMETERS
----------
Filter Type:     ${geneFilterType.replace('geneSymbol', 'symbol')}
GSEA Params:     ${JSON.stringify(gseaParams, null, 2)}

========================`;
}

export function createEnrichmentInfoContent({ searchType, text, enrichmentParams, geneFilterType }) {
  return `ENRICHMENT INFORMATION
========================

Download Date:   ${new Date().toLocaleString()}

QUERY
-----
Search Type:     ${searchType}
Query Text:      ${text}

PARAMETERS
----------
Filter Type:     ${searchType === 'GENES' ? (geneFilterType === 'name' ? 'symbol' : geneFilterType.replace('geneSymbol', 'symbol')) : 'strID'}
Custom Params:   ${JSON.stringify(enrichmentParams, null, 2)}

========================`;
}
